import { useId, type SVGProps } from "react";

/**
 * The prize mark (Figma `prize / icon`, mark=star, tone=on-dark): a rounded five-point star in
 * the prize gold gradient, drawn for the prize badge — no disc, no per-type icon. Sized by its
 * container (`em` in `PrizeText`, so it scales with the type) and painted from the gold tokens
 * rather than baked hexes, so a partner retheme of the gold carries into the mark. The emboss is
 * the parent's — `PrizeText` drop-shadows mark and figure together.
 */
/** The marks drawn so far: the star (every prize) and the crown (holding the top prize, on the
 *  launcher's leaderboard wheel); a per-campaign-type mark comes later. */
export type PrizeMarkKind = "star" | "crown";

export const PrizeMark = ({
  mark = "star",
  tone = "gold",
  ...props
}: SVGProps<SVGSVGElement> & {
  mark?: PrizeMarkKind;
  /** `ink`: the surrounding text colour (the star on a gold surface, `PrizeText tone="onGold"`). */
  tone?: "gold" | "ink";
}) => {
  const gradientId = useId();
  return (
    <svg viewBox="4 6 56 52" aria-hidden focusable="false" {...props}>
      <defs>
        <linearGradient
          id={gradientId}
          x1="32"
          y1="6"
          x2="32"
          y2="58"
          gradientUnits="userSpaceOnUse"
        >
          <stop
            offset="0"
            style={{ stopColor: "var(--prize-gold-light, #fff6d5)" }}
          />
          <stop
            offset="0.45"
            style={{ stopColor: "var(--prize-gold, #ffd86a)" }}
          />
          <stop
            offset="1"
            style={{ stopColor: "var(--prize-gold-deep, #e0a92a)" }}
          />
        </linearGradient>
      </defs>
      {mark === "crown" ? (
        // Three points with a jewel on each tip, over a band — the same box and gold as the star.
        <g fill={tone === "ink" ? "currentColor" : `url(#${gradientId})`}>
          <path d="M13 45 9.5 21.5 22.5 32 32 13 41.5 32 54.5 21.5 51 45Z" />
          <rect x="13" y="47.5" width="38" height="6.5" rx="2.5" />
          <circle cx="9.5" cy="19.5" r="3.6" />
          <circle cx="32" cy="10.5" r="4" />
          <circle cx="54.5" cy="19.5" r="3.6" />
        </g>
      ) : (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          fill={tone === "ink" ? "currentColor" : `url(#${gradientId})`}
          d="M30.4245 8.28184C31.4748 5.60834 32.5252 5.60834 33.5755 8.28184L38.1586 19.9688C39.2089 22.6423 41.1663 24.065 44.0307 24.2369L56.5771 24.9816C59.4415 25.1535 59.7662 26.1465 57.551 27.9607L47.8405 35.9812C45.6253 37.7953 44.8805 40.0869 45.6062 42.8559L48.7571 55.0585C49.4827 57.8274 48.6425 58.4385 46.2364 56.8917L35.6092 50.1316C33.2031 48.5848 30.7969 48.5848 28.3908 50.1316L17.7636 56.8917C15.3575 58.4385 14.5173 57.8274 15.2429 55.0585L18.3938 42.8559C19.1195 40.0869 18.3747 37.7953 16.1595 35.9812L6.44903 27.9607C4.23385 26.1465 4.55849 25.1535 7.42294 24.9816L19.9693 24.2369C22.8337 24.065 24.7911 22.6423 25.8414 19.9688L30.4245 8.28184Z"
        />
      )}
    </svg>
  );
};
